import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, createPageAbortController } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Factory, Loader2, ShieldCheck, ClipboardList, HardHat, ArrowLeft, LogIn, X } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhdXRvbWF0ZWQlMjBtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeSUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3ODU4MTkzNDN8MA&ixlib=rb-4.1.0&q=85";

// Visual identity per tier — each role is deliberately distinct.
const TIERS = {
  admin: {
    label: "Administrator",
    blurb: "Full control · settings, users & data",
    icon: ShieldCheck,
    accent: "text-amber-600",
    ring: "ring-2 ring-amber-400/70",
    hover: "hover:border-amber-400 hover:shadow-amber-200/60",
    chip: "bg-amber-100 text-amber-700",
    avatar: "bg-gradient-to-br from-amber-400 to-orange-500 text-white",
    big: true,
  },
  planner: {
    label: "Production Planners",
    blurb: "Schedules, purchasing & orders",
    icon: ClipboardList,
    accent: "text-blue-600",
    ring: "ring-1 ring-blue-300",
    hover: "hover:border-blue-400 hover:shadow-blue-200/60",
    chip: "bg-blue-100 text-blue-700",
    avatar: "bg-gradient-to-br from-blue-500 to-indigo-600 text-white",
    big: false,
  },
  operator: {
    label: "Operators",
    blurb: "Read-only floor views",
    icon: HardHat,
    accent: "text-slate-500",
    ring: "ring-1 ring-slate-200",
    hover: "hover:border-slate-400 hover:shadow-slate-200/60",
    chip: "bg-slate-100 text-slate-600",
    avatar: "bg-gradient-to-br from-slate-500 to-slate-700 text-white",
    big: false,
  },
};

const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export default function Login() {
  const { login } = useAuth();
  const [profiles, setProfiles] = useState({ admin: [], planner: [], operator: [] });
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [selected, setSelected] = useState(null); // { name, username, role }
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Centralized abort-on-unmount: the controller auto-aborts on pagehide/beforeunload and
    // api.js maps unload-time "Failed to fetch" rejections to an AbortError-like sentinel,
    // so a cancelled request never logs console noise or sets state on a dead component.
    const controller = createPageAbortController();
    api.get("/auth/profiles", { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setProfiles({ admin: data.admin || [], planner: data.planner || [], operator: data.operator || [] }); })
      .catch((e) => {
        if (api.isAbort(e) || controller.signal.aborted) return;
        logger.error("Failed to load login profiles:", e);
      })
      .finally(() => { if (!controller.signal.aborted) setLoadingProfiles(false); });
    return () => controller.cleanup();
  }, [setProfiles, setLoadingProfiles]);

  const pick = (u) => { setSelected(u); setPassword(""); setError(""); };
  const back = () => { setSelected(null); setPassword(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setError("");
    setLoading(true);
    try {
      await login(selected.username, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const Tier = ({ roleKey }) => {
    const cfg = TIERS[roleKey];
    const list = profiles[roleKey] || [];
    if (list.length === 0) return null;
    const Icon = cfg.icon;
    return (
      <section className="mb-8" data-testid={`profile-tier-${roleKey}`}>
        <div className="flex items-center gap-2 mb-3">
          <Icon className={`h-4 w-4 ${cfg.accent}`} />
          <h3 className={`font-display text-sm font-bold uppercase tracking-wider ${cfg.accent}`}>{cfg.label}</h3>
          <span className="text-[11px] text-slate-400 font-normal normal-case">— {cfg.blurb}</span>
          <div className="h-px flex-1 bg-slate-200 ml-2" />
        </div>
        <div className={`grid gap-3 ${cfg.big ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}>
          {list.map((u) => (
            <button
              key={u.username}
              type="button"
              onClick={() => pick(u)}
              data-testid={`profile-card-${u.username}`}
              className={`group flex items-center gap-4 rounded-xl bg-white border border-slate-200 ${cfg.ring} ${cfg.hover} text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${cfg.big ? "p-5" : "p-4"}`}
            >
              <span className={`flex items-center justify-center rounded-full font-bold shadow-sm ${cfg.avatar} ${cfg.big ? "h-14 w-14 text-lg" : "h-11 w-11 text-sm"}`}>
                {initials(u.name)}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block font-semibold text-slate-800 break-words leading-tight ${cfg.big ? "text-lg" : "text-sm"}`}>{u.name}</span>
                <span className="block text-xs text-slate-400 font-mono-data truncate">@{u.username}</span>
              </span>
              <span className={`shrink-0 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${cfg.chip}`}>{roleKey}</span>
            </button>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-form-container">
      {/* Left – brand / imagery */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-slate-900">
        <img src={HERO} alt="factory" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-blue-900/60" />
        <div className="relative z-10 flex items-center gap-3 text-white">
          <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center shadow-lg shadow-blue-900/40 ring-1 ring-white/30 overflow-hidden">
            <img src="/hk-logo.jpg" alt="H. K. Industries logo" className="h-full w-full object-contain p-1" data-testid="login-logo" />
          </div>
          <div>
            <p className="font-display text-xl font-bold tracking-tight">H. K. INDUSTRIES</p>
            <p className="text-xs text-slate-300 tracking-widest uppercase">Industrial Operations</p>
          </div>
        </div>
        <div className="relative z-10 text-white">
          <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            Plan, schedule &amp; sync your factory floor in real time.
          </h1>
          <p className="mt-4 text-slate-300 text-base max-w-md leading-relaxed">
            Live production scheduling, capacity intelligence and role-based control for modern manufacturing teams.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-slate-300 font-mono-data">
            <span>• Real-time WebSocket sync</span>
            <span>• RBAC secured</span>
          </div>
        </div>
      </div>

      {/* Right – profile selector */}
      <div className="flex items-start justify-center p-6 sm:p-12 bg-slate-50 overflow-y-auto">
        <div className="w-full max-w-lg py-4">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <Factory className="h-5 w-5" />
            </div>
            <p className="font-display text-lg font-bold">H. K. INDUSTRIES</p>
          </div>

          <h2 className="font-display text-3xl font-bold text-slate-900">Choose Your Profile</h2>
          <p className="text-sm text-slate-500 mt-1">Select who you are, then enter your password to sign in.</p>

          <div className="mt-8">
            {loadingProfiles ? (
              <div className="flex items-center justify-center py-20 text-slate-400" data-testid="profiles-loading">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <>
                <Tier roleKey="admin" />
                <Tier roleKey="planner" />
                <Tier roleKey="operator" />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Password sheet – shown when a card is picked */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="login-password-sheet">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={back} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
            <button type="button" onClick={back} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="login-password-close">
              <X className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <span className={`flex items-center justify-center rounded-full h-16 w-16 text-xl font-bold shadow ${TIERS[selected.role]?.avatar || "bg-slate-600 text-white"}`}>
                {initials(selected.name)}
              </span>
              <p className="mt-3 font-display text-lg font-bold text-slate-900">{selected.name}</p>
              <p className="text-xs text-slate-400 font-mono-data">@{selected.username}</p>
              <span className={`mt-2 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${TIERS[selected.role]?.chip || "bg-slate-100 text-slate-600"}`}>{selected.role}</span>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <Input
                type="password"
                autoFocus
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-form-password-input"
                required
              />
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>
              )}
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={back} data-testid="login-back-button">
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="login-form-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-1.5" /> Sign In</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
